/**
 * RescoreLeadsButton — bulk "Re-score with AI" for product segmentation.
 * Shows a credit-cost preview before queuing Brain re-qualification jobs.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { Sparkles, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

export function RescoreLeadsButton() {
  const [open, setOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);

  const openDialog = async () => {
    setOpen(true);
    setLoading(true);
    try {
      const res = await api.get('/api/leads/rescore-preview');
      setPreview(res.data);
    } catch (e) {
      toast.error('Could not load re-score preview', { description: e?.response?.data?.detail || '' });
      setOpen(false);
    } finally { setLoading(false); }
  };

  const run = async () => {
    setRunning(true);
    try {
      const res = await api.post('/api/leads/rescore-all');
      const d = res.data || {};
      toast.success(`Re-scoring queued for ${d.queued} leads`, {
        description: d.skipped_budget > 0
          ? `${d.skipped_budget} leads skipped — credit budget reached. Est. cost: ${d.estimated_credits} Cr.`
          : `Est. cost: ${d.estimated_credits} Cr. Product segments will appear as the Brain works through the queue.`,
      });
      setOpen(false);
    } catch (e) {
      toast.error('Re-score failed', { description: e?.response?.data?.detail || 'Please try again.' });
    } finally { setRunning(false); }
  };

  return (
    <>
      <Button variant="ghost" size="icon" className="h-8 w-8" onClick={openDialog} title="Re-score all leads with AI (product segmentation)" data-testid="rescore-leads-button">
        <Sparkles className="h-4 w-4" />
      </Button>
      <AlertDialog open={open} onOpenChange={setOpen}>
        <AlertDialogContent data-testid="rescore-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2"><Sparkles className="h-4 w-4 text-primary" />Re-score all leads with AI</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm">
                {loading ? (
                  <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" />Calculating cost…</span>
                ) : preview ? (
                  <>
                    <p>The Brain will re-qualify every open lead against your ICP <span className="font-medium">and your Products & Services catalog</span>, segmenting each lead by best-fit offering.</p>
                    <div className="rounded-md border border-border bg-muted/40 p-3 space-y-1 text-xs" data-testid="rescore-preview-box">
                      <div className="flex justify-between"><span>Open leads to re-score</span><span className="font-semibold">{preview.lead_count}</span></div>
                      <div className="flex justify-between"><span>Cost per lead</span><span className="font-semibold">{preview.cost_per_lead} Cr</span></div>
                      <div className="flex justify-between"><span>Estimated total</span><span className="font-semibold">{preview.estimated_credits} Cr</span></div>
                      <div className="flex justify-between"><span>Your balance</span><span className="font-semibold">{preview.credit_balance} Cr</span></div>
                      {!preview.overage_enabled && preview.affordable_count < preview.lead_count && (
                        <p className="text-amber-600 pt-1">Balance covers ~{preview.affordable_count} leads — the rest will be skipped until you top up.</p>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">Runs safely in the background. Duplicate protection: each lead is re-scored at most once per day.</p>
                  </>
                ) : null}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="rescore-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); run(); }} disabled={loading || running || !preview?.lead_count} data-testid="rescore-confirm">
              {running ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}Re-score {preview?.lead_count || ''} leads
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
